import { FC, Fragment } from 'react'
import { ApolloError } from '@apollo/client'
import { omitBy } from 'lodash'
import styled, { css } from 'styled-components'
import { ActivityOrderField, RecruitListQuery } from 'generated/graphql'
import NoticeBannerRecruitInternStatusDesktop from '@app.feature/ad/desktop/NoticeBanner/RecruitIntern/NoticeBannerRecruitInternStatus.desktop'
import useCheckQueryParamsForRecruitAds from '@app.feature/ad/hooks/useCheckQueryParamsForRecruitAds'
import { ActivityTypeID } from 'constants/enums'
import RecruitListSkeleton from '../RecruitListSkeleton'
import RecruitListItemDesktop from './component/RecruitListItem.desktop'

type TProps = {
  activityType: ActivityTypeID
  loading: boolean
  currentOrderByField: ActivityOrderField
  filters: ReturnType<typeof omitBy>
  data?: RecruitListQuery
  error?: ApolloError
}

const ListTableDesktop: FC<TProps> = ({
  loading,
  data,
  error,
  filters,
  currentOrderByField,
  activityType,
}) => {
  const nodes = data?.activities?.nodes || []

  function renderList() {
    if (error) return null

    if (loading) {
      return <RecruitListSkeleton />
    }

    if (nodes.length === 0) {
      return (
        <tr>
          <td colSpan={7}>
            <div className="list-no-data">검색결과가 없습니다.</div>
          </td>
        </tr>
      )
    }

    return (
      <>
        {nodes.map((node) => (
          <Fragment key={node.id}>
            <RecruitListItemDesktop
              activity={node}
              currentUser={data?.currentUser}
            />
          </Fragment>
        ))}
      </>
    )
  }

  const { isShowNoticeBanner } = useCheckQueryParamsForRecruitAds({
    filters,
    currentOrderByField,
    hasActivities: nodes.length > 0,
  })

  return (
    <StyledWrapper>
      <table className="recruit-list-table">
        <caption>공고 리스트</caption>
        <colgroup>
          <col data-col="recruit-company" />
          <col data-col="recruit-name" />
          <col data-col="recruit-type" />
          <col data-col="location" />
          <col data-col="recruit-close" />
          <col data-col="view-count" />
          <col data-col="scrap" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              <p>기업명</p>
            </th>
            <th scope="col">
              <p>공고명</p>
            </th>
            <th scope="col">
              <p>채용형태</p>
            </th>
            <th scope="col">
              <p>근무지역</p>
            </th>
            <th scope="col">
              <p>마감일</p>
            </th>
            <th scope="col">
              <p>조회수</p>
            </th>
            <th scope="col">
              <p className="text-blind">스크랩</p>
            </th>
          </tr>
        </thead>
        <tbody>
          {isShowNoticeBanner && (
            <NoticeBannerRecruitInternStatusDesktop
              activityType={activityType}
            />
          )}
          {renderList()}
        </tbody>
      </table>
    </StyledWrapper>
  )
}

export default ListTableDesktop

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    background-color: #ffffff;

    .recruit-list-table {
      width: 100%;
      height: 100%;
      table-layout: fixed;

      &:first-child {
        border-top: 0;
      }

      caption {
        width: 0;
        height: 0;
        font-size: 0;
      }

      colgroup {
        col {
          &[data-col='recruit-company'] {
            width: 20%;
          }
          &[data-col='recruit-name'] {
          }
          &[data-col='recruit-type'] {
            width: 10%;
          }
          &[data-col='location'] {
            width: 10%;
          }
          &[data-col='recruit-close'] {
            width: 10%;
          }
          &[data-col='view-count'] {
            width: 8%;
          }
          &[data-col='scrap'] {
            width: 5%;
          }
        }
      }

      thead {
        tr {
          border-top: 1px solid #999999;

          th {
            padding: 16px 0;
            text-align: center;

            p {
              font-size: 15px;
              color: ${theme.color.text_1};
              font-weight: 500;
              letter-spacing: -0.5px;

              &.text-blind {
                font-size: 0;
              }
            }
          }
        }
      }

      tbody {
        tr {
          border-top: 1px solid ${theme.background.bg_1};

          td {
            max-height: 100px;
            font-size: 16px;
            padding: 20px 0;
            text-align: center;
            line-height: 16px;

            &:nth-child(2) {
              text-align: left;
              padding: 20px;
            }

            .list-no-data {
              text-align: center;
              padding: 5px;
              border-bottom: 0;
            }

            &[colspan='7'] {
              padding: 0;

              .ad-manager-container {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
              }
              iframe {
                height: 102px;
              }
            }
          }
        }
      }
    }
  `}
`
